import {
  Box,
  Button,
  type SelectChangeEvent,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { BaseTableList } from 'components/AgGrid/AgGrid';

import { PaddedListWrapper } from 'components/AgGrid/CellRenderer/ListView.styled';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import DtcHandler from 'handlers/dtc.handler';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hook';
import { type DTC } from 'types/dtc.types';
import {
  convertUtcToDisplayTime,
  formatAge,
  formatStartEndDateUTC,
} from 'utils/helpers/dateTime';
import { DTCChart } from './DTC/Chart';
import { DTCCodeDisplay, VehicleDisplay } from './DTCCellrenderer';
import { IsActiveFilter } from './IsActiveFilter';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import ExportSelector from 'views/FleetOverview/components/FilterViewBar/ExportSelector';
import saveAs from 'file-saver';
import { useCustomDropdownEffect } from 'utils/exports/hooks';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import CustomTimeZoneDateRangePicker from 'components/DatePIcker/CustomTimeZoneDateRangePicker';
import { filterDtcByWords } from 'views/PlanningAid/components/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

dayjs.extend(utc);
dayjs.extend(timezone);

// list of suffix and prefix need to trimmed
const prefixesToTrimFromDTC = ['CDL', 'j1939', 'j1708', 'kj1939'];
const suffixesToTrimFromDTC = ['DTC'];

// Helper to calculate unique values
const getUniqueValues = (
  field: string,
  filteredDTC: Array<Record<string, any>> = []
) => {
  return Array.from(new Set(filteredDTC.map((row) => row[field]))).length;
};

export const DTCList = ({ enableDarkTheme }: { enableDarkTheme: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state?.data;
  const dtcHandler = new DtcHandler();
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);

  // selectors
  const { allDTC, allGroupedDTCData, isLoading } = useAppSelector(
    (state) => state.dtcReducer
  );
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  const { preferredTimeDisplayMode, timezone, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();

  // state
  const [selectedAsset, setSelectedAsset] = useState(null);
  const selectedAssetRef = useRef(selectedAsset);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStartDate, setStartDate] = useState(
    new Date(new Date().getTime() - 5 * 24 * 60 * 60 * 1000)
  );
  const [selectedEndDate, setEndDate] = useState(new Date());

  // functions
  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const getDtcData = async (startDate: Date, endDate: Date) => {
    const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
      'Custom',
      startDate,
      endDate,
      timezoneBasedOnDisplayMode
    );

    if (!startUtcISO || !endUtcISO) {
      return;
    }
    const payload = {
      startDate: startUtcISO,
      endDate: endUtcISO,
    };
    await Promise.all([
      dtcHandler.getAllData(payload),
      dtcHandler.getAllGroupedData(payload),
    ]);
  };

  const handleClickReset = () => {
    navigate('/diagnostic-trouble-codes');
  };

  // effects
  useEffect(() => {
    const fetchData = async () => {
      if (
        selectedStartDate &&
        selectedEndDate &&
        accessToken &&
        timezoneBasedOnDisplayMode
      ) {
        await getDtcData(selectedStartDate, selectedEndDate);
      }
    };

    void fetchData();
  }, [
    selectedStartDate,
    selectedEndDate,
    accessToken,
    timezoneBasedOnDisplayMode,
  ]);

  // vars

  const [exportDropdownTo, setExportDropdownTo] = useState('');

  const [exportToLabel, setExportToLabel] = useState(t('export_to'));
  const handleExportDropDownChange = (event: SelectChangeEvent) => {
    setExportDropdownTo(event.target.value);
  };

  const [exportTo, setExportTo] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleExportClick = (val: boolean) => {
    setExportTo(val);
  };
  const filteredAndSortedDTC = useMemo(() => {
    try {
      if (!Array.isArray(allGroupedDTCData)) {
        console.error('Invalid data, exp => arr', allGroupedDTCData);
        return [];
      }

      let temp = allGroupedDTCData;

      if (
        locationState?.bumperNumber &&
        locationState?.isActive !== undefined
      ) {
        // Filter if both are present
        temp = allGroupedDTCData.filter(
          (dtc: DTC) =>
            dtc?.asset === locationState?.bumperNumber &&
            dtc?.is_active === locationState?.isActive
        );
      } else if (locationState?.bumperNumber) {
        // Filter only if bumperNumber is present
        temp = allGroupedDTCData.filter(
          (dtc: DTC) => dtc?.asset === locationState?.bumperNumber
        );
      }

      if (locationState?.sensorFilter) {
        temp = filterDtcByWords(allGroupedDTCData, locationState.sensorFilter, [
          'dtc_type',
        ]);
      }

      // ensuring temp is an arr, else early return
      if (!Array.isArray(temp)) {
        console.error('Filtered result is not an array,', temp);
        return [];
      }

      // Sort descending by last_occurrence
      return [...temp].sort((a, b) =>
        dayjs(b?.last_occurrence).isAfter(dayjs(a?.last_occurrence)) ? 1 : -1
      );
    } catch (error) {
      console.error('Error while filtering and sorting DTC data,', error);
      return [];
    }
  }, [allGroupedDTCData, locationState?.bumperNumber, locationState?.isActive]);
  const filteredAndSortedAllDTC = useMemo(() => {
    if (!Array.isArray(allDTC)) {
      console.error('Invalid data, exp => arr', allDTC);
      return [];
    }
    const temp = [...allDTC];

    temp.sort((a, b) =>
      dayjs(b?.start_ts).isAfter(dayjs(a?.start_ts)) ? 1 : -1
    );
    return temp;
  }, [allDTC]);
  const dtcSummaryText = useMemo(() => {
    if (locationState?.bumperNumber) {
      if (locationState?.isActive !== undefined) {
        return `Showing ${locationState.isActive ? 'Active' : ''} DTCs for ${
          locationState.bumperNumber
        }`;
      }
      return `Showing all DTCs for ${locationState.bumperNumber}`;
    }
    if (locationState?.sensorFilter) {
      return `Showing all DTCs for Planning Aid`;
    }
    return '';
  }, [locationState?.bumperNumber, locationState?.isActive]);

  const columnDefs = [
    {
      field: 'asset',
      headerName: t('dtc_col_vehicle'),
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        maxNumConditions: getUniqueValues('asset', filteredAndSortedDTC),
      },
      sortable: true,
      cellRenderer: VehicleDisplay,
      cellRendererParams: (params: any) => {
        const assetValue = params.data.asset;
        const matchedAsset = assetsWithLiveData.find(
          (item) => item.bumperNumber === assetValue
        );
        return {
          field: 'asset',
          latestTagData,
          asset: matchedAsset,
          handleVehicleClick: async (
            asset: string,
            startTs: string,
            dtcType: string
          ) => {
            const selectedAsset = assets.find(
              (item) => item.bumperNumber === asset
            );
            if (selectedAsset) {
              const dataToPass = {
                assetVin: selectedAsset.assetVin,
                alertStartTs: startTs,
                tagNames: [dtcType],
              };
              let encodedData = encodeURIComponent(JSON.stringify(dataToPass));
              encodedData = encodedData.replace(/%/g, '%25');
              navigate(
                `/sensor-analysis?assetVin=${selectedAsset.assetVin}&data=${encodedData}`
              );
            }
          },
        };
      },
    },
    {
      field: 'dtc_code',
      headerName: t('fault_code'),
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        maxNumConditions: getUniqueValues('dtc_code', filteredAndSortedDTC),
      },
      sortable: true,
      cellRenderer: DTCCodeDisplay,
      tooltipValueGetter: (params: any) => {
        if (params.data.failure_reason) {
          const formattedString = `
          Sensor Type: ${params.data.dtc_type}
          Fault Code: ${params.data.dtc_code}
          Failure Reason: ${params.data.failure_reason}
          Recommendations:
          - ${params.data.recommendations.slice(1, -1).split(', ').join('\n- ')}
        `;
          return `${formattedString}`;
        }
      },
      comparator: (valueA: string, valueB: string) => {
        const aParts = valueA.split('-').map(Number);
        const bParts = valueB.split('-').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] ?? 0;
          const bVal = bParts[i] ?? 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      },
    },
    {
      field: 'dtc_type',
      headerName: t('sensor_type'),
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        maxNumConditions: getUniqueValues('dtc_type', filteredAndSortedDTC),
      },
      valueGetter: (params: any) => {
        const rawValue = params?.data?.dtc_type;
        if (typeof rawValue !== 'string') {
          // early return incase of non string
          return '';
        }
        let value = rawValue;
        // Trim known prefixes
        for (const prefix of prefixesToTrimFromDTC) {
          if (value?.toLowerCase()?.startsWith(prefix?.toLowerCase())) {
            // conversion to lower case - for case insensetivity
            value = value?.slice(prefix.length)?.trim();
            break;
          }
        }
        // Trim known suffixes
        for (const suffix of suffixesToTrimFromDTC) {
          if (value?.toLowerCase()?.endsWith(suffix?.toLowerCase())) {
            // conversion to lower case - for case insensetivity
            value = value?.slice(0, -suffix.length)?.trim();
            break;
          }
        }
        return value;
      },
      sortable: true,
    },
    {
      field: 'dtc_description',
      headerName: t('fault_description'),
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        maxNumConditions: getUniqueValues(
          'dtc_description',
          filteredAndSortedDTC
        ),
      },
      sortable: true,
    },
    {
      field: 'is_active',
      headerName: t('cdp_step_status'),
      filter: IsActiveFilter,
      filterParams: {
        defaultFilter: 'active', // This will be used to set the default filter
      },
      sortable: true,
      valueFormatter: (params: any) =>
        params.value ? t('active') : t('inactive'),
    },
    {
      field: 'first_occurrence',
      headerName: t('first_occurrence'),
      filter: 'agDateColumnFilter',
      sortable: true,
      valueGetter: (params: any) => {
        return params.data.first_occurrence
          ? dayjs.utc(params.data.first_occurrence).toDate()
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value
          ? convertUtcToDisplayTime(
              params.data.first_occurrence,
              preferredTimeDisplayMode,
              timezone,
              DATE_TIME_FORMAT
            )
          : '';
      },
    },
    {
      field: 'last_occurrence',
      headerName: t('last_occurrence'),
      filter: 'agDateColumnFilter',
      sortable: true,
      valueGetter: (params: any) => {
        return params.data.last_occurrence
          ? dayjs.utc(params.data.last_occurrence).toDate()
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value
          ? convertUtcToDisplayTime(
              params.data.last_occurrence,
              preferredTimeDisplayMode,
              timezone,
              DATE_TIME_FORMAT
            )
          : '';
      },
    },
    {
      field: 'total_occurrences',
      headerName: t('total_occurrences'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) => params.data.total_occurrences,
    },
    {
      field: 'min_age',
      headerName: t('min_duration'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: any) => formatAge(params?.value),
      comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
        const secsA = Number(nodeA.data?.min_age_seconds ?? 0);
        const secsB = Number(nodeB.data?.min_age_seconds ?? 0);
        return secsA - secsB;
      },
    },
    {
      field: 'max_age',
      headerName: t('max_duration'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: any): string => {
        return `${formatAge(params?.value)}`;
      },
      comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
        const secsA = Number(nodeA.data?.max_age_seconds ?? 0);
        const secsB = Number(nodeB.data?.max_age_seconds ?? 0);
        return secsA - secsB;
      },
    },
  ];

  const maxEndDate = useMemo(() => {
    if (!selectedStartDate) return new Date();
    const twoMonthsInMs = 60 * 24 * 60 * 60 * 1000;
    const maxDate = new Date(selectedStartDate.getTime() + twoMonthsInMs);
    const today = new Date();
    return maxDate < today ? maxDate : today;
  }, [selectedStartDate]);

  // const exportFormat = 'excel';
  useCustomDropdownEffect(
    exportDropdownTo,
    handleExportClick,
    setExportToLabel,
    setExportDropdownTo,
    [exportDropdownTo]
  );
  useEffect(() => {
    const exportData = async () => {
      if (!exportTo || !selectedStartDate || !selectedEndDate) return;
      const downloadEngineHours = async () => {
        setIsDownloading(true);
        try {
          const startApiDate = selectedStartDate.toISOString().slice(0, 10);
          const endApiDate = selectedEndDate.toISOString().slice(0, 10);
          const bytes = await dtcHandler.downloadEngineHoursExcel(
            startApiDate,
            endApiDate,
            exportDropdownTo as 'excel' | 'pdf'
          );

          const fileExtencsion = exportDropdownTo === 'excel' ? 'xlsx' : 'pdf';

          const blob = new Blob([bytes], {
            type:
              exportDropdownTo === 'excel'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'application/pdf',
          });
          saveAs(
            blob,
            `DTC-Report-${convertUtcToDisplayTime(
              selectedStartDate,
              preferredTimeDisplayMode,
              timezone,
              'YYYY-MM-DD'
            )} to ${convertUtcToDisplayTime(
              selectedEndDate,
              preferredTimeDisplayMode,
              timezone,
              'YYYY-MM-DD'
            )}.${fileExtencsion}`
          );
        } finally {
          setIsDownloading(false);
          // turn it off so we don't re-run
          handleExportClick(false);
        }
      };
      switch (exportDropdownTo) {
        case 'excel':
        case 'pdf': {
          void downloadEngineHours();
          break;
        }
        default: {
          break;
        }
      }
    };

    void exportData();
  }, [exportTo, exportDropdownTo, preferredTimeDisplayMode, timezone]);
  useEffect(() => {
    selectedAssetRef.current = selectedAsset;
  }, [selectedAsset]);

  return (
    <>
      {isLoading ? (
        <div
          style={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SingleFadeLoader />
        </div>
      ) : (
        <Box
          sx={{
            paddingBottom: '20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              gap: '8px',
              justifyContent: 'space-between',
              padding: '0px 20px',
              height: '30px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: (enableDarkTheme ? _palette.dark : _palette.light)
                    .textPrimary,
                }}
              >
                {t('dtc_started_within')}
              </Typography>
              <CustomTimeZoneDateRangePicker
                startDate={selectedStartDate}
                endDate={selectedEndDate}
                onFinalDateChange={handleDateChange}
                maxDate={dayjs(maxEndDate)}
                format={DATE_FORMAT}
              />
            </div>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 0',
              }}
            >
              <ExportSelector
                view={''}
                exportToLabel={exportToLabel}
                exportTo={exportDropdownTo}
                onExportToChange={handleExportDropDownChange}
                itemArr={['excel', 'pdf']}
                enableDarkTheme={enableDarkTheme}
                isPrinting={isDownloading}
              />
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2">
                      {t(
                        'diagnostic_trouble_codes_dtcs_are_codes_that_identify_specif'
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t(
                        'this_page_shows_active_and_historical_dtcs_with_details_like'
                      )}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      bgcolor: enableDarkTheme
                        ? _palette.dark.toolBarBackground
                        : '#fff',
                      color: enableDarkTheme ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                      '& .MuiTooltip-arrow': {
                        color: enableDarkTheme
                          ? _palette.dark.toolBarBackground
                          : '#fff',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    color: enableDarkTheme ? '#fff' : 'rgba(0, 0, 0, 0.54)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {dtcSummaryText ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <p
                  style={{
                    color: (enableDarkTheme ? _palette.dark : _palette.light)
                      .textPrimary,
                  }}
                >
                  {dtcSummaryText}
                </p>
                <div>
                  <Button
                    sx={{
                      color: '#FFFFFF',
                      border: `1px solid ${_palette.dark.borderGrey}`,
                      backgroundColor: enableDarkTheme
                        ? _palette.dark.primary
                        : _palette.light.primary,
                      '&:hover': {
                        backgroundColor: enableDarkTheme
                          ? _palette.dark.primaryDark
                          : _palette.light.primaryDark,
                      },
                      fontSize: '12px',
                      textTransform: 'none',
                      height: '30px',
                    }}
                    onClick={handleClickReset}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
          {filteredAndSortedDTC.length < 1 ? (
            <Typography
              sx={{
                color: (enableDarkTheme ? _palette.dark : _palette.light)
                  .textPrimary,
                paddingLeft: '20px',
                marginTop: '20px',
              }}
            >
              {t('dtc_data_not_available')}
            </Typography>
          ) : (
            <>
              <PaddedListWrapper>
                <BaseTableList
                  rowData={filteredAndSortedDTC}
                  columnDefs={columnDefs}
                  rowCursorPointer={true}
                  cacheQuickFilter={true}
                  pagination={true}
                  paginationPageSize={10}
                  tableStyles={{
                    height: '510px',
                  }}
                  rowSelection={true}
                  onGridReady={(params) => {
                    const gridApi = params.api;
                    const columnApi = params.columnApi;

                    // Set default filter to show only active DTCs
                    const statusColumn = columnApi.getColumn('is_active');
                    if (statusColumn) {
                      const statusFilter =
                        gridApi.getFilterInstance('is_active');
                      if (statusFilter) {
                        statusFilter.setModel({ value: 'active' });
                        gridApi.onFilterChanged();
                      }
                    }

                    // Existing row click handler
                    gridApi.addEventListener('rowClicked', (event: any) => {
                      if (selectedAssetRef.current !== event.data.asset) {
                        setSelectedType(null);
                      }
                      setSelectedAsset(event.data.asset);
                      event.node.setSelected(true);
                    });
                  }}
                />
              </PaddedListWrapper>
              <DTCChart
                enableDarkTheme={enableDarkTheme}
                data={filteredAndSortedAllDTC}
                selectedAsset={selectedAsset}
                setSelectedAsset={setSelectedAsset}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
              />
            </>
          )}
        </Box>
      )}
    </>
  );
};
